import { Observable } from 'rxjs'
import { filter, map } from 'rxjs/operators'
import { ofType, StateObservable } from 'redux-observable'
import { GlobalState } from 'store'
import { RepositoryActionTypes, RepositoryActionUpdateField } from 'store/repository'
import { EntityType, Negotiation, NegotiationActions, NegotiationFieldMeta, ownOrderToSide, UserPreference } from 'api'
import { applyUpdatesForWindow } from 'model'
import { NegotiationFieldNames } from 'view/routes/requests/LiveNegotiationsGrid/LiveGridColumnNames'
import { sliceFieldName } from 'utils'
import { negotiationRepositoryEntryGetterSelector } from 'selectors/negotiations'
import { getNegotiatedValue } from 'view/routes/requests/shared/utils'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { isNil } from 'lodash-es'
import { BTQAction } from 'epics'

export const NegotiationStartDateFieldName = sliceFieldName(NegotiationFieldNames.START_DATE_MODIFIED, 1)
export const NegotiationEndDateFieldName = sliceFieldName(NegotiationFieldNames.END_DATE_MODIFIED, 1)
export const NegotiationInitiatorIdmFieldName = sliceFieldName(NegotiationFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER_MODIFIED, 1)
export const NegotiationInitiatorEdmFieldName = sliceFieldName(NegotiationFieldNames.INITIATOR_EXECUTION_DECISION_MAKER_MODIFIED, 1)
export const NegotiationResponderIdmFieldName = sliceFieldName(NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_MODIFIED, 1)
export const NegotiationResponderEdmFieldName = sliceFieldName(NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_MODIFIED, 1)
export const NegotiationStartConsiderationOverride = sliceFieldName(NegotiationFieldNames.START_CONSIDERATION_OVERRIDE_MODIFIED, 1)

const NegotiableFieldLookups = {
  [sliceFieldName(NegotiationFieldNames.RATE_MODIFIED, 1)]: NegotiationFieldMeta.RATE,
  [sliceFieldName(NegotiationFieldNames.HAIRCUT_TYPE_MODIFIED, 1)]: NegotiationFieldMeta.HAIRCUT_TYPE,
  [sliceFieldName(NegotiationFieldNames.HAIRCUT_MODIFIED, 1)]: NegotiationFieldMeta.HAIRCUT,
  [sliceFieldName(NegotiationFieldNames.NOTIONAL_MODIFIED, 1)]: NegotiationFieldMeta.NOTIONAL,
  [sliceFieldName(NegotiationFieldNames.START_DATE_MODIFIED, 1)]: NegotiationFieldMeta.START_DATE,
  [sliceFieldName(NegotiationFieldNames.START_CONSIDERATION_MODIFIED, 1)]: NegotiationFieldMeta.START_CONSIDERATION,
  [sliceFieldName(NegotiationFieldNames.END_DATE_MODIFIED, 1)]: NegotiationFieldMeta.END_DATE,
  [sliceFieldName(NegotiationFieldNames.AXE, 1)]: NegotiationFieldMeta.IS_AXED,
  [sliceFieldName(NegotiationFieldNames.ROLL_UNWIND, 1)]: NegotiationFieldMeta.TRADE_CONDITION_FLAG,
  [sliceFieldName(NegotiationFieldNames.TERM_MODIFIED, 1)]: NegotiationFieldMeta.TERM,
  [sliceFieldName(NegotiationFieldNames.BOND_PRICE_MODIFIED, 1)]: NegotiationFieldMeta.BOND_PRICE,
  [sliceFieldName(NegotiationFieldNames.RFQ_TYPE_MODIFIED, 1)]: NegotiationFieldMeta.RFQ_TYPE,
  [sliceFieldName(NegotiationFieldNames.COUNTERPARTY_SPONSOR_ID_MODIFIED, 1)]: NegotiationFieldMeta.COUNTERPARTY_SPONSOR_ID,
  [sliceFieldName(
    NegotiationFieldNames.INITIATOR_EXECUTION_DECISION_MAKER_MODIFIED,
    1
  )]: NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER,
  [sliceFieldName(
    NegotiationFieldNames.RESPONDER_EXECUTION_DECISION_MAKER_MODIFIED,
    1
  )]: NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER,
  [sliceFieldName(
    NegotiationFieldNames.INITIATOR_INVESTMENT_DECISION_MAKER_MODIFIED,
    1
  )]: NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER,
  [sliceFieldName(
    NegotiationFieldNames.RESPONDER_INVESTMENT_DECISION_MAKER_MODIFIED,
    1
  )]: NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER,
  [sliceFieldName(NegotiationFieldNames.PRE_AGREED_MODIFIED, 1)]: NegotiationFieldMeta.PRE_AGREED,
  [sliceFieldName(NegotiationFieldNames.COUNTERPARTY_NAME_MODIFIED, 1)]: NegotiationFieldMeta.COUNTERPARTY_ID,

  [sliceFieldName(NegotiationFieldNames.INITIATOR_BOOK_MODIFIED, 1)]: NegotiationFieldMeta.INITIATOR_BOOK,
  [sliceFieldName(NegotiationFieldNames.INITIATOR_STRATEGY_MODIFIED, 1)]: NegotiationFieldMeta.INITIATOR_STRATEGY,
  [sliceFieldName(NegotiationFieldNames.RESPONDER_BOOK_MODIFIED, 1)]: NegotiationFieldMeta.RESPONDER_BOOK,
  [sliceFieldName(NegotiationFieldNames.RESPONDER_STRATEGY_MODIFIED, 1)]: NegotiationFieldMeta.RESPONDER_STRATEGY,
  [sliceFieldName(NegotiationFieldNames.START_CONSIDERATION_OVERRIDE_MODIFIED, 1)]: NegotiationFieldMeta.START_CONSIDERATION_OVERRIDE,
  [sliceFieldName(NegotiationFieldNames.FEE_MODIFIED, 1)]: NegotiationFieldMeta.FEE,
  [sliceFieldName(NegotiationFieldNames.SECONDARY_BASKET_ID_MODIFIED, 1)]: NegotiationFieldMeta.SECONDARY_BASKET_ID,
  [sliceFieldName(NegotiationFieldNames.SETTLEMENT_VENUE_MODIFIED, 1)]: NegotiationFieldMeta.SETTLEMENT_VENUE
}

export function negotiationModifyEpic(action$: Observable<BTQAction>, state$: StateObservable<GlobalState>): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, RepositoryActionTypes.REPOSITORY_UPDATE_FIELD, RepositoryActionUpdateField>(
      RepositoryActionTypes.REPOSITORY_UPDATE_FIELD
    ),
    filter(action => action.payload.entityType === EntityType.NEGOTIATION),
    map(action => {
      const ownOrder = action.meta.ownOrder ?? true
      const repositoryEntries = negotiationRepositoryEntryGetterSelector(state$.value)(ownOrderToSide(ownOrder))
      const entry = repositoryEntries[action.payload.entityId]
      const negotiation = applyUpdatesForWindow(entry, action.meta.sourceWindowId)
      const userPreference = createUserPreferenceSelector()(state$.value)

      return createUpdateValuesAction(action, ownOrder, negotiation, userPreference)
    })
  )
}

function createUpdateValuesAction(
  action: RepositoryActionUpdateField,
  ownOrder: boolean,
  negotiation: Negotiation,
  userPreference: UserPreference
) {
  const negotiableField = NegotiableFieldLookups[action.payload.fieldName]
  if (!negotiableField) {
    throw new Error(`Unsupported negotiable field ${action.payload.fieldName} - do you need add it to the lookups?`)
  }

  if (
    [
      NegotiationInitiatorEdmFieldName,
      NegotiationInitiatorIdmFieldName,
      NegotiationResponderEdmFieldName,
      NegotiationResponderIdmFieldName
    ].includes(action.payload.fieldName)
  ) {
    return NegotiationActions.updateRegulatoryValues(
      negotiation.negotiationId,
      getDecisionMakerValue(true, action.payload.fieldName, action.payload.fieldValue as string, negotiation),
      getDecisionMakerValue(false, action.payload.fieldName, action.payload.fieldValue as string, negotiation),
      ownOrder,
      action.meta.sourceWindowId
    )
  }

  if (action.payload.fieldName === NegotiationStartConsiderationOverride) {
    return NegotiationActions.updateValues(
      negotiation.negotiationId,
      ownOrder,
      {
        [negotiableField]: action.payload.fieldValue,
        [NegotiationFieldMeta.START_CONSIDERATION]: null,
        [NegotiationFieldMeta.NOTIONAL]: null
      },
      isNil(action.payload.rawValue) ? {} : { [negotiableField]: action.payload.rawValue },
      { haircutSign: userPreference.haircutSign },
      action.meta.sourceWindowId
    )
  }

  return NegotiationActions.updateValues(
    negotiation.negotiationId,
    ownOrder,
    { [negotiableField]: action.payload.fieldValue },
    isNil(action.payload.rawValue) ? {} : { [negotiableField]: action.payload.rawValue },
    { haircutSign: userPreference.haircutSign },
    action.meta.sourceWindowId
  )
}

function getDecisionMakerValue(idmField: boolean, fieldName: string, fieldValue: string, negotiation: Negotiation) {
  switch (fieldName) {
    case NegotiationInitiatorEdmFieldName:
      return !idmField ? fieldValue : getNegotiatedValue(negotiation.initiatorInvestmentDecisionMaker)
    case NegotiationInitiatorIdmFieldName:
      return idmField ? fieldValue : getNegotiatedValue(negotiation.initiatorExecutionDecisionMaker)
    case NegotiationResponderEdmFieldName:
      return !idmField ? fieldValue : getNegotiatedValue(negotiation.responderInvestmentDecisionMaker)
    case NegotiationResponderIdmFieldName:
      return idmField ? fieldValue : getNegotiatedValue(negotiation.responderExecutionDecisionMaker)
    default:
      throw new Error(`Invalid field name [${fieldName}]`)
  }
}
